import React, { memo, useRef, useState } from 'react'
import { ApolloError, useLazyQuery } from '@moonpig/web-core-graphql'
import { useRouter, RouterType } from '@moonpig/web-core-routing'
import {
  AutoCompleteVariant,
  DepartmentsEnum,
  Platform,
} from '@moonpig/web-core-types-graphql'
import { styled, breakpointDown } from '@moonpig/launchpad-utils'
import { triggerLuxInit } from '@moonpig/web-core-utils'
import type {
  SubmitItemType,
  ReminderItemType,
  UrlSuggestionItemType,
  SuggestionItemType,
  RecentSearchType,
} from '@moonpig/web-core-nav'
import type { SearchDepartment } from '@moonpig/web-core-nav/src/Search'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { usePlatform } from '@moonpig/web-core-context'
import { GetSearchSuggestionsQuery } from './suggestions/__generated__/Suggestions'
import { useLocaleText } from '../SearchBar.locale'
import {
  clearSearchHistory,
  getSearchHistory,
  removeSearchHistoryItem,
  SearchHistoryItem,
} from './searchHistory'
import {
  FlatNavigationalSuggestion,
  getNavigationalSuggestions,
} from './navigationalSuggestions'
import {
  GetSearchSuggestionsGQL,
  SUGGESTIONS_LIMIT,
  SearchSuggestion,
  mapDepartmentSuggestions,
} from './suggestions/Suggestions'
import { TrackedSearch } from './TrackedSearch'
import { useSearchInspirations } from './searchInspirations'
import { useSearchReminders } from './searchReminders'
import { groupAndParseInspirations } from './searchInspirations/utils/groupAndParseInspirations'
import { allowSmartDepartmentRedirect } from './smartDepartmentRedirection'

export const MAX_RECENT_SEARCHES = 3
const DEFAULT_RECENT_SEARCH_DEPARTMENT = 'all_cards'
const DEFAULT_RECENT_SEARCH_DEPARTMENT_TITLE = 'Cards'

const mapToRecentSearchType = (
  history: SearchHistoryItem[],
): RecentSearchType[] =>
  history.slice(0, MAX_RECENT_SEARCHES).map(x => ({
    department: x.department,
    searchTerm: x.searchTerm,
    value: x.searchTerm,
  }))

const getRecentSearchesFromHistory = (): RecentSearchType[] =>
  mapToRecentSearchType(getSearchHistory())

const StyledDiv = styled.div`
  ${breakpointDown('md')} {
    z-index: 99;
    position: relative;
  }
`

type SearchBarDepartment = {
  name: string
  title: string
}

type SearchBarProps = {
  searchDepartment?: SearchBarDepartment
  searchTerm?: string
  onApolloError?: (error: ApolloError) => void
}

const redirectToSearchPage = ({
  value,
  searchKey,
  searchDepartment,
  suggestionDepartment,
  smartRedirection,
  useServerSideSDR,
  isAllDepartmentSelected,
  submitItem,
  router,
}: {
  value: string
  searchKey?: string
  searchDepartment?: SearchBarDepartment
  suggestionDepartment?: SearchBarDepartment
  smartRedirection: boolean
  useServerSideSDR: boolean
  isAllDepartmentSelected: boolean
  submitItem: SubmitItemType
  router: RouterType
}) => {
  const { params } = router.getCurrentRoute<'content'>()
  const { region } = params

  window.scrollTo(0, 0)

  const department =
    useServerSideSDR && isAllDepartmentSelected
      ? 'all'
      : suggestionDepartment?.name ||
        searchDepartment?.name ||
        /* istanbul ignore next */ 'all_cards'

  const newParams = {
    ...params,
    offset: undefined,
    suggestion: undefined,
    trending_suggestion: undefined,
    parts: undefined,
    q: undefined,
    filters: undefined,
    redirectedDepartment: undefined,
    region,
    d: department,
    ...(submitItem.itemType === 'suggestion' && {
      suggestion: searchKey || value,
    }),
    ...(submitItem.itemType === 'inspiration' && {
      trending_suggestion: value,
    }),
    ...(!searchKey && submitItem.itemType !== 'inspiration' && { q: value }),
    ...(smartRedirection && { smartRedirection: true }),
  }

  return router.push({
    name: 'search',
    params: newParams,
  })
}

const SearchBarComponent: React.FC<SearchBarProps> = ({
  searchDepartment,
  searchTerm,
  onApolloError,
}) => {
  const router = useRouter()
  const localeText = useLocaleText()
  const [suggestions, setSuggestions] = useState([] as SearchSuggestion[])
  const [navSuggestions, setNavSuggestions] = useState(
    [] as FlatNavigationalSuggestion[],
  )

  const minNumberOfCharacters = 1

  const { isMobile } = usePlatform()

  const useServerSideSDR = useExperiment('search-server-side-sdr', {
    fallback: false,
  })

  const searchSuggestionsPlatform: Platform = isMobile
    ? Platform.MOBILE_WEB
    : Platform.WEB

  const searchValue = useRef(searchTerm || '')

  const [fetchSearchSuggestions, { loading }] =
    useLazyQuery<GetSearchSuggestionsQuery>(GetSearchSuggestionsGQL, {
      onError: onApolloError,
      onCompleted: data => {
        const transformedSuggestions = mapDepartmentSuggestions(
          data.searchSuggest.suggestions,
        )

        setSuggestions(transformedSuggestions)
      },
    })

  const inspirations = useSearchInspirations()

  const reminders = useSearchReminders().map(x => ({
    id: x.id,
    value: x.label,
    occasion: x.occasion,
    date: x.date,
  }))

  const [recentSearches, setRecentSearches] = useState(
    getRecentSearchesFromHistory,
  )

  const removeRecentSearch = (recentSearch: RecentSearchType) => {
    setRecentSearches(
      mapToRecentSearchType(removeSearchHistoryItem(recentSearch)),
    )
  }

  const clearRecentSearches = () => {
    clearSearchHistory()
    setRecentSearches([])
  }

  const transformNavSuggestions = (props: {
    searchTerm: string
    router: RouterType
  }) => {
    const { params } = router.getCurrentRoute<'content'>()
    const { region: regionName } = params
    const navigationalSuggestions = getNavigationalSuggestions(
      props.searchTerm,
      regionName,
    )

    const transformedNavSuggestions = navigationalSuggestions?.map(x => ({
      value: x.displayText,
      url: x.url(regionName),
      icon: x.icon,
    }))
    setNavSuggestions(transformedNavSuggestions ?? [])
  }

  const handleSubmit = (submitItem: SubmitItemType) => {
    triggerLuxInit()
    const { itemType, value } = submitItem
    if (itemType === 'reminder') {
      const reminder = submitItem as ReminderItemType
      const { params } = router.getCurrentRoute<'content'>()

      return router.push({
        name: 'reminder-landing',
        params: {
          region: params.region,
          id: reminder.id,
        },
      })
    }

    if (submitItem.itemType === 'navigational') {
      const suggestion = submitItem as UrlSuggestionItemType
      searchValue.current = submitItem.value
      window.location.assign(suggestion.url)
    }

    let searchKey: string | undefined
    let department: SearchDepartment | SearchBarDepartment | undefined

    if (itemType === 'suggestion') {
      const suggestion = submitItem as SuggestionItemType
      searchKey = suggestion.searchKey
      department = suggestion.department

      searchValue.current = suggestion.value
    }

    if (itemType === 'search' || itemType === 'search-fallback') {
      department = submitItem.department
    }

    if (itemType === 'inspiration' || itemType === 'recent') {
      department = {
        name: submitItem.department as string,
        title: '',
      }
    }

    const isAllDepartmentSelected =
      (department &&
        'activeDepartment' in department &&
        department.activeDepartment?.name === 'all') ||
      department?.name === 'all'

    department =
      department?.name === 'all'
        ? { name: 'all_cards', title: 'Cards' }
        : department

    let departmentFromItem =
      submitItem.itemType === 'search' || !submitItem.itemType
        ? submitItem.department
        : undefined

    /* istanbul ignore next */
    if (submitItem.itemType === 'recent') {
      departmentFromItem = {
        name: submitItem.department || 'all_cards',
        enum: (submitItem.department as DepartmentsEnum) || 'all_cards',
        title: '',
      }
    }

    if (submitItem.itemType === 'inspiration' && submitItem.department) {
      departmentFromItem = {
        name: submitItem.department,
        enum:
          (submitItem.department as DepartmentsEnum) ||
          /* istanbul ignore next */ 'all_cards',
        title: submitItem.department,
      }
    }

    const { suggestion } = router.getCurrentRoute<'search'>().params

    const shouldApplySmartDepartmentRedirect = allowSmartDepartmentRedirect({
      itemType: submitItem.itemType,
      params: {
        suggestion: suggestion || /* istanbul ignore next */ '',
      },
      department: department
        ? { ...department, enum: department.name as DepartmentsEnum }
        : /* istanbul ignore next */ departmentFromItem,
    })

    redirectToSearchPage({
      value,
      searchKey,
      searchDepartment: departmentFromItem || searchDepartment,
      suggestionDepartment: department,
      smartRedirection: !useServerSideSDR && shouldApplySmartDepartmentRedirect,
      useServerSideSDR,
      isAllDepartmentSelected,
      submitItem,
      router,
    })
  }

  const handleKeyUp = (value: string) => {
    const shouldFetchSuggestions =
      value.length >= minNumberOfCharacters && searchValue.current !== value

    /* istanbul ignore else */
    if (shouldFetchSuggestions) {
      searchValue.current = value

      transformNavSuggestions({ searchTerm: value, router })

      fetchSearchSuggestions({
        variables: {
          searchTerm: value,
          limit: SUGGESTIONS_LIMIT,
          loadCounts: true,
          experimentValues: 'search-use-new-ia-index=true',
          platform: searchSuggestionsPlatform,
          filters: { hasFaceSwap: false },
          autoCompleteVariant: AutoCompleteVariant.V2,
        },
      })
    } else if (suggestions.length && value.length < minNumberOfCharacters) {
      searchValue.current = ''
      setSuggestions([])
    }
  }

  const onSearchBarFocus = () => {
    setRecentSearches(getRecentSearchesFromHistory())

    if (searchValue.current.length > minNumberOfCharacters) {
      const experimentValues = 'search-use-new-ia-index=true'

      fetchSearchSuggestions({
        variables: {
          searchTerm: searchValue.current,
          limit: SUGGESTIONS_LIMIT,
          loadCounts: true,
          experimentValues: experimentValues as string,
          platform: searchSuggestionsPlatform,
          filters: { hasFaceSwap: false },
          autoCompleteVariant: AutoCompleteVariant.V2,
        },
      })
    }
  }

  return (
    <StyledDiv>
      <TrackedSearch
        items={suggestions}
        searchTerm={searchTerm}
        searchValue={searchValue.current}
        onSubmit={handleSubmit}
        onKeyUp={handleKeyUp}
        onFocus={onSearchBarFocus}
        onClear={() => {
          searchValue.current = ''
        }}
        navigationalSuggestions={navSuggestions}
        navigationalHeaderText={localeText('navigational_suggestion_header')}
        maxNavigationalSuggestions={1}
        onSuggestionsVisible={
          // istanbul ignore next
          () => {}
        }
        searchDepartment={
          searchDepartment || {
            name: DEFAULT_RECENT_SEARCH_DEPARTMENT,
            title: DEFAULT_RECENT_SEARCH_DEPARTMENT_TITLE,
          }
        }
        inspirations={groupAndParseInspirations(inspirations)}
        onRemoveRecentSearch={removeRecentSearch}
        onClearRecentSearches={clearRecentSearches}
        onRemindersVisible={
          // istanbul ignore next
          () => {}
        }
        onTabChange={
          // istanbul ignore next
          () => {}
        }
        reminders={reminders}
        recentSearches={recentSearches}
        suggestionsLoading={loading}
      />
    </StyledDiv>
  )
}

export const SearchBar = memo(SearchBarComponent)
